import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { debounce, kebabCase } from 'lodash-es';

import { Box, Flex, Heading, LinkBox, LinkOverlay, List, ListItem, useBreakpointValue } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ContentContainer } from '~/components/container';

import { TitleMarkdownSection } from './title-markdown-section';

export type ContentSectionProps = {
  sections: Array<{ title: string; body: string }>;
};

export function ContentSection({ sections }: ContentSectionProps) {
  const [activeId, setActiveId] = useState<string>();
  const isTableOfContentsSynced = useBreakpointValue({
    base: false,
    md: true,
  });
  useEffect(() => {
    if (!isTableOfContentsSynced) return;
    const elements = sections.map((section) => document.getElementById(kebabCase(section.title)));
    const handleIntersection = debounce((entry: IntersectionObserverEntry) => {
      const id = entry.target.id;
      setActiveId(id);
    }, 200);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            handleIntersection(entry);
          }
        }
      },
      {
        threshold: 0,
        rootMargin: '-64px 0px -85% 0px',
      }
    );

    for (const element of elements) {
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      for (const element of elements) {
        if (element) {
          observer.unobserve(element);
        }
      }
    };
  }, [sections, isTableOfContentsSynced]);

  if (!sections?.length) return null;

  return (
    <Box
      as='section'
      bgColor='white'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer
        display='flex'
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: '40px',
          md: 12,
        }}
      >
        <Box
          borderRight={{
            md: '1px solid #6491FC33',
          }}
          flex={{
            md: '0 1 295px',
          }}
          paddingRight={{
            md: 12,
          }}
        >
          <TableOfContents activeId={activeId} isTableOfContentsSynced={isTableOfContentsSynced} sections={sections} />
        </Box>

        <Flex
          flex='1'
          flexDirection='column'
          position='relative'
          gap={{
            base: 12,
            md: '72px',
          }}
        >
          {sections.map((section) => (
            <TitleMarkdownSection key={section.title} body={section.body} title={section.title} />
          ))}
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function TableOfContents({
  sections,
  activeId,
  isTableOfContentsSynced,
}: {
  sections: Array<{ title: string; body: string }>;
  activeId?: string;
  isTableOfContentsSynced?: boolean;
}) {
  const router = useRouter();
  return (
    <Box as='nav' position='sticky' top={`calc(${headerHeight} + 16px)`}>
      <Heading as='p' color='blue.500' mb={6} size='marketing-xl'>
        Table of Contents
      </Heading>
      <List>
        {sections.map((section) => {
          const id = kebabCase(section.title);
          const isActive = isTableOfContentsSynced ? activeId === id : router.asPath.endsWith(`#${id}`);
          return (
            <ListItem key={id}>
              <LinkBox
                bgColor={isActive ? 'blue.100' : 'transparent'}
                borderLeft='4px solid'
                borderLeftColor={isActive ? 'blue.500' : 'transparent'}
                padding='12px 16px'
                transition='background-color 0.3s ease-in-out, border-color 0.3s ease-in-out'
              >
                <LinkOverlay as={Link} fontWeight='500' href={`#${id}`}>
                  {section.title}
                </LinkOverlay>
              </LinkBox>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
